import React from 'react'
import { SocialButton } from '@/components/ui/social-button'

type ColorVariant = {
  container: string
  text: string
  icon: string
  outline: string
}

type SocialLinks = {
  linkedin?: string
  x?: string
  facebook?: string
  instagram?: string
}

export interface AuthorCardProps {
  name: string
  position?: string
  avatar?: string
  content: React.ReactNode
  variant?: 'primary' | 'secondary' | 'tertiary' | 'transparent'
  socialLinks?: SocialLinks
}

export const AUTHOR_CARD_VARIANTS: Record<string, ColorVariant> = {
  primary: {
    container: 'bg-green-50',
    text: 'text-teal-500',
    icon: 'bg-green-100',
    outline: 'border-teal-400',
  },
  secondary: {
    container: 'bg-[#C4D9DD]',
    text: 'text-[#008DA4]',
    icon: 'bg-green-100',
    outline: 'border-teal-600',
  },
  tertiary: {
    container: 'bg-[#C3D1D7]',
    text: 'text-[#00577E]',
    icon: 'bg-green-100',
    outline: 'border-green-500/50',
  },
  transparent: {
    container: 'bg-transparent',
    text: 'text-inherit',
    icon: 'bg-transparent',
    outline: 'border-transparent',
  },
}

export const AuthorCard = ({
  name,
  position,
  avatar,
  content,
  variant = 'primary',
  socialLinks,
}: AuthorCardProps) => {
  const color = AUTHOR_CARD_VARIANTS[variant] ?? AUTHOR_CARD_VARIANTS.primary

  return (
    <div
      className={`flex flex-col items-start rounded-[4rem] md:rounded-full box-border ${color.container} mt-6 p-6 px-8 md:flex-row md:items-center md:space-x-6`}
    >
      <div className="flex-shrink-0 mb-4 md:mb-0">
        <div className="relative flex items-center justify-center">
          <div
            className={`absolute inset-0 rounded-full border-2 ${color.outline}`}
            aria-hidden="true"
          />
          {avatar ? (
            <img
              className="h-24 w-24 md:h-36 md:w-36 rounded-full object-cover"
              src={avatar}
              alt={`Avatar of ${name}`}
              loading="lazy"
            />
          ) : (
            <div className="h-24 w-24 md:h-36 md:w-36 rounded-full bg-gray-100 flex items-center justify-center">
              <span className="text-gray-500 text-xl font-medium">
                {name
                  .split(' ')
                  .map(n => n[0])
                  .join('')
                  .toUpperCase()}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="space-y-4">
        <div>
          <div className="text-lg font-bold text-gray-900">{name}</div>
          {position && (
            <div
              className={`text-sm font-medium uppercase tracking-wide ${color.text}`}
            >
              {position}
            </div>
          )}
        </div>
        <div className="font-display text-gray-700 text-sm tracking-wide leading-relaxed">
          {content}
        </div>
        {socialLinks && (
          <div className="flex items-center justify-start space-x-2">
            {socialLinks.linkedin && (
              <a
                href={socialLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
              >
                <SocialButton
                  type="linkedin"
                  url={socialLinks.linkedin || ''}
                />
              </a>
            )}
            {socialLinks.x && (
              <a href={socialLinks.x} target="_blank" rel="noopener noreferrer">
                <SocialButton type="x" url={socialLinks.linkedin || ''} />
              </a>
            )}
            {socialLinks.facebook && (
              <a
                href={socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
              >
                <SocialButton
                  type="facebook"
                  url={socialLinks.linkedin || ''}
                />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

AuthorCard.displayName = 'AuthorCard'
